import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { exportPrefixes } from './api/prefixApi'
import ConfirmDialog, { confirmDialog } from '../../components/ui/ConfirmDialog'
import { Paginator } from 'primereact/paginator'
import DataTable from '../../components/ui/DataTable'
import EmptyState from '../../components/ui/EmptyState'
import ExcelExportButton from '../../components/ui/ExcelExportButton'
import PageHeader from '../../components/ui/PageHeader'
import RefreshButton from '../../components/ui/RefreshButton'
import { useToast } from '../../components/ui/ToastProvider'
import { getApiErrorCode, isOptimisticLock } from '../../api/apiError'
import { getPrefixImpact } from './api/prefixApi'
import { prefixColumns } from './components/PrefixColumns'
import PrefixFormDialog from './components/PrefixFormDialog'
import type { PrefixFormValues } from './components/prefixFormSchema'
import { usePrefixes } from './hooks/usePrefixes'
import type { Prefix } from './types'
import { CARD_VIEW_BREAKPOINT, useMediaQuery } from '../../lib/useMediaQuery'
import Button from '../../components/ui/Button'
import { useProxyMode } from '../proxy/useProxyMode'
// Paylaşılan layout sınıfları (admin-page/admin-form/admin-row-actions).
import '../admin/admin.css'

/** Varsayılan sayfa boyutu (anayasa: tüm listelerde 10). */
const PAGE_SIZE = 10

/**
 * Tutanak → Parametreler → **Ön Ek Tanımları** (056). `REF_PREFIX` için **salt liste + ad/ön ek
 * düzenleme**.
 *
 * Bilinçli olarak **yok**: "Yeni ekle", "Sil", durum sütunu/toggle'ı, arama/filtre çubuğu ve kolon
 * sıralaması (FR-002/FR-002a/FR-005). Kayıt kümesi veritabanı tarafında yönetilir; liste 10 satırlık
 * sabit kümedir ve düzeni `ID` artandır.
 *
 * **Koşullu onay (FR-010a):** ön ek gerçekten değiştiyse kaydetme, etkilenen kayıt sayısını gösteren bir
 * onay adımından geçer; yalnız ad değiştiyse doğrudan kaydedilir. Sayı hesaplanamazsa (veya uç hata
 * verirse) onay **sayı olmadan** yine gösterilir — koruma adımı atlanmaz.
 *
 * Yetki: `PREFIX_MANAGE`.
 */
export default function PrefixesPage() {
  const { t } = useTranslation()
  const toast = useToast()
  // 126 — Kısıtlı Yetkili asistan ekranı SALT OKUNUR görür: düzenle hiç çizilmez.
  const { readOnly } = useProxyMode()

  const [first, setFirst] = useState(0)
  const [rows, setRows] = useState(PAGE_SIZE)
  const [editing, setEditing] = useState<Prefix | null>(null)
  const [prefixError, setPrefixError] = useState<string | null>(null)
  /** Etki sayısı çekilirken Kaydet devre dışı — ikinci bir onay penceresi doğmasın (İptal açık kalır). */
  const [checkingImpact, setCheckingImpact] = useState(false)

  /**
   * Açık olan satırın **canlı** değeri. `onSubmit` içindeki closure render anındaki `editing`'i taşır;
   * `getPrefixImpact` yanıtı geldiğinde form kapanmış ya da başka satıra geçilmiş olabilir. Bu ref o anki
   * gerçeği söyler → geç gelen onay penceresi yanlış satıra yazmaz.
   */
  const editingRef = useRef<Prefix | null>(null)

  const { list, update } = usePrefixes({ page: Math.floor(first / rows), size: rows })

  const items = list.data?.content ?? []
  const total = list.data?.page.totalElements ?? 0
  // Dar ekranda (eşik: CARD_VIEW_BREAKPOINT — lib/useMediaQuery) tablo yerine kart listesi — aynı veri/sayfalama/düzenle akışı
  // (bkz. ui.css `.ui-card-list`).
  const isMobile = useMediaQuery(CARD_VIEW_BREAKPOINT)

  /** Açık satırı tek yerden değiştir (state + ref birlikte) — ikisi asla ayrışmaz. */
  const setEditingRow = (row: Prefix | null) => {
    editingRef.current = row
    setEditing(row)
  }

  const openEdit = (row: Prefix) => {
    setPrefixError(null)
    setEditingRow(row)
  }

  const closeForm = () => {
    setPrefixError(null)
    setEditingRow(null)
  }

  const save = (row: Prefix, values: PrefixFormValues) => {
    update.mutate(
      { id: row.id, body: { name: values.name, prefix: values.prefix, modDate: row.modDate } },
      {
        onSuccess: () => {
          toast.success(t('prefixes.toast.updated'))
          closeForm()
        },
        onError: (err) => {
          // Yinelenen ön ek → alan altında gösterilir (merkezî toast `handledCodes` ile bastırılmıştır).
          if (getApiErrorCode(err) === 'DUPLICATE_CODE') {
            setPrefixError(t('prefixes.errors.duplicatePrefix'))
            return
          }
          // Optimistic-lock/generic toast'ı merkezî hata yönetimi (019) basar; ekran yalnız tazeler.
          if (isOptimisticLock(err)) {
            closeForm()
            list.refetch()
          }
        },
      },
    )
  }

  const onSubmit = (values: PrefixFormValues) => {
    const row = editing
    if (!row) return
    setPrefixError(null)

    // Ön ek DEĞİŞMEDİYSE (harf büyüklüğü dahil birebir) onay adımı çıkmaz — zararsız etiket düzeltmesi.
    if (values.prefix === row.prefix) {
      save(row, values)
      return
    }

    // Ön ek değişti → kaydetmeden önce etkilenen kayıt sayısıyla onay al (FR-010a).
    // Sayı çekilemezse (uç hatası veya null) onay sayı olmadan gösterilir; koruma atlanmaz.
    setCheckingImpact(true)
    getPrefixImpact(row.id)
      .then((impact) => impact.affectedCount)
      .catch(() => null)
      .then((count) => {
        setCheckingImpact(false)
        // Yanıt gecikirken form kapanmış ya da BAŞKA satıra geçilmiş olabilir → onay açılmaz.
        // (Aksi halde geç gelen onay, artık düzenlenmeyen satıra yazabilirdi.)
        if (editingRef.current?.id !== row.id) return
        confirmDialog({
          header: t('prefixes.confirm.title'),
          message:
            count == null
              ? t('prefixes.confirm.messageNoCount', { from: row.prefix, to: values.prefix })
              : t('prefixes.confirm.message', {
                  from: row.prefix,
                  to: values.prefix,
                  count,
                }),
          icon: 'pi pi-exclamation-triangle',
          acceptLabel: t('prefixes.confirm.accept'),
          // Yalnız `accept` yazma yapar; iptalde form girdileriyle AÇIK kalır.
          accept: () => save(row, values),
        })
      })
  }

  return (
    <div className="admin-page">
      <ConfirmDialog />

      <PrefixFormDialog
        visible={editing != null}
        initial={editing}
        submitting={update.isPending}
        submitDisabled={update.isPending || checkingImpact}
        prefixError={prefixError}
        onSubmit={onSubmit}
        onHide={closeForm}
      />

      <PageHeader
        title={t('nav.tutanak.prefixes')}
        actions={
          <>
            <RefreshButton onRefresh={() => list.refetch()} />
            <ExcelExportButton onExport={exportPrefixes} disabled={items.length === 0} />
          </>
        }
      />

      {/* Sunucu-taraflı sayfalama (lazy). Kolon sıralaması kapalı → sortField/onSort verilmez. */}
      {isMobile ? (
        <div className="ui-card-list">
          {list.isLoading &&
            Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="ui-card ui-card--skeleton" aria-hidden />
            ))}
          {!list.isLoading && items.length === 0 && (
            <EmptyState title={t('prefixes.empty.title')} description={t('prefixes.empty.desc')} />
          )}
          {!list.isLoading &&
            items.map((row) => (
              <div key={row.id} className="ui-card">
                <div className="ui-card-top">
                  <span className="ui-card-sub">{row.prefix}</span>
                  {!readOnly && (
                    <div className="ui-card-actions ui-card-actions--compact">
                      <Button
                        icon="pi pi-pencil"
                        rounded
                        text
                        aria-label={t('prefixes.actions.edit')}
                        onClick={() => openEdit(row)}
                      />
                    </div>
                  )}
                </div>
                <div className="ui-card-head">
                  <span className="ui-card-title">{row.name}</span>
                </div>
              </div>
            ))}
          {!list.isLoading && total > rows && (
            <Paginator
              first={first}
              rows={rows}
              totalRecords={total}
              onPageChange={(e) => {
                setFirst(e.first)
                setRows(e.rows)
              }}
              template="PrevPageLink CurrentPageReport NextPageLink"
              currentPageReportTemplate={t('common.pageReport')}
              className="ui-cards-paginator"
            />
          )}
        </div>
      ) : (
        <DataTable
          value={items}
          loading={list.isLoading}
          lazy
          first={first}
          rows={rows}
          totalRecords={total}
          onPage={(e) => {
            setFirst(e.first)
            setRows(e.rows)
          }}
          emptyMessage={
            <EmptyState title={t('prefixes.empty.title')} description={t('prefixes.empty.desc')} />
          }
        >
          {prefixColumns({ t, onEdit: openEdit, readOnly })}
        </DataTable>
      )}
    </div>
  )
}
